import * as React from "react";
import PropTypes from "prop-types";
import { useEffect } from "react";
import {
  alpha,
  useMediaQuery,
  useTheme,
  styled,
  InputBase,
  ButtonGroup,
} from "@mui/material";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  ListItemText,
  Checkbox,
  IconButton,
  Paper,
  Tooltip,
  Collapse,
  CircularProgress,
  circularProgressClasses,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { PictureAsPdf, Description, FilterList } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { visuallyHidden } from "@mui/utils";
import { KeyboardArrowUp, Search as SearchIcon } from "@mui/icons-material";
import BTTypography from "../BTTypography";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useLoading } from "../../context";
import BTBox from "../BTBox";
function useDebounce(value, delay = 500) {
  const [debounceValue, setDebouncedValue] = React.useState(value);
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(id);
    };
  }, [value, delay]);
  return debounceValue;
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
  transition: theme.transitions.create("width"),
  overflow: "hidden",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    fontSize: "15px",
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "20ch",
    },
  },
}));
function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    columns,
    headerCellStyle,
    headerRowStyle,
    checkBoxSelected,
    expandRow,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow
        sx={{
          // background:
          //   headerRowStyle?.background ||
          //   "linear-gradient(195deg, #CED4DA 100%)",
          "& .MuiTableCell-head": {
            color: "black",
            fontWeight: "bold",
            background:
              headerRowStyle?.background ||
              "linear-gradient(195deg, #CED4DA 100%)", // For gradient continuity
          },
        }}
      >
        {checkBoxSelected && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                "aria-label": "select all items",
              }}
            />
          </TableCell>
        )}
        {expandRow && <TableCell sx={{ width: "10px" }}></TableCell>}
        {columns.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={
              headCell?.center ? "center" : headCell?.numeric ? "right" : "left"
            }
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              ...headerCellStyle,
              ...headCell.headerCellStyle,
              color: headerRowStyle?.color || "black",
              fontWeight: "bold",
              width: headCell.width,
              textAlign: headCell?.center
                ? "center"
                : headCell?.numeric
                ? "right"
                : "left",
            }}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.name || headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.name || headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number,
  columns: PropTypes.array.isRequired,
  headerCellStyle: PropTypes.object,
  headerRowStyle: PropTypes.object,
  checkBoxSelected: PropTypes.bool,
  expandRow: PropTypes.bool,
};

function EnhancedTableToolbar(props) {
  const {
    numSelected,
    title,
    onDelete,
    onSearch,
    globalSearch,
    onExportPDF,
    onExportExcel,
    columns,
    onToggleColumn,
    visibleColumns,
    selectedConfiguration,
  } = props;
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const debouncedSearchText = useDebounce(searchText);
  const [isSearch, setSearch] = React.useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setSearch(true);
  };
  const handleColumnToggle = (columnId) => (event) => {
    event.stopPropagation();
    onToggleColumn(columnId);
  };
  useEffect(() => {
    if (isSearch) {
      onSearch(debouncedSearchText);
    }
  }, [debouncedSearchText]);

  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 2 },
        },
        numSelected > 0 && {
          bgcolor: selectedConfiguration?.bgColor
            ? selectedConfiguration?.bgColor
            : (theme) =>
                alpha(
                  theme.palette.primary.main,
                  theme.palette.action.activatedOpacity
                ),
        },
      ]}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="button"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {title}
        </Typography>
      )}
      <Box display={"flex"} alignItems={"center"}>
        {searchOpen && (
          <Search>
            <StyledInputBase
              placeholder="Search..."
              inputProps={{ "aria-label": "search" }}
              value={searchText}
              onChange={handleSearchChange}
              autoFocus
              onBlur={() => searchText === "" && setSearchOpen(false)}
            />
          </Search>
        )}
        <ButtonGroup
          variant="contained"
          size="small"
          sx={{ ml: 1, height: "30px", boxShadow: "none" }}
          color="dark"
        >
          {globalSearch && (
            <Tooltip title="Filter list">
              <IconButton onClick={(e) => setSearchOpen(!searchOpen)}>
                <SearchIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Export as PDF">
            <IconButton onClick={onExportPDF}>
              <PictureAsPdf />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export as Excel">
            <IconButton onClick={onExportExcel}>
              <Description />
            </IconButton>
          </Tooltip>
          <Tooltip title="Visible Columns">
            <IconButton
              aria-controls={open ? "demo-customized-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? true : undefined}
              size="small"
              onClick={handleClick}
            >
              <FilterList />
            </IconButton>
          </Tooltip>
        </ButtonGroup>
        {numSelected > 0 && (
          <>
            <BTBox display={"flex"} gap={1}>
              {selectedConfiguration?.buttons?.map((button, index) =>
                React.cloneElement(button, {
                  key: index,
                  onClick: (e) => {
                    if (button.props.onClick) {
                      button.props.onClick(e, onDelete());
                    } else {
                      onDelete();
                    }
                  },
                })
              )}
            </BTBox>
            {(!selectedConfiguration?.buttons ||
              selectedConfiguration.buttons.length === 0) && (
              <Tooltip title="Delete">
                <IconButton onClick={onDelete}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: 200,
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ px: 2, py: 1, fontWeight: "bold" }}
        >
          Visible Columns
        </Typography>
        {columns?.map((column) => (
          <MenuItem
            key={column.id}
            dense
            onClick={handleColumnToggle(column.id)}
          >
            <Checkbox
              checked={visibleColumns.includes(column.id)}
              onChange={() => handleColumnToggle(column.id)}
              color="secondary"
              size="small"
              sx={{ py: 0 }}
            />
            <ListItemText
              primary={column.name || column.label || column.id}
              sx={{
                "& .css-1236rad-MuiTypography-root": {
                  fontSize: "14px",
                },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  title: PropTypes.string,
  onDelete: PropTypes.func,
  onSearch: PropTypes.func,
  globalSearch: PropTypes.bool,
  columns: PropTypes.array.isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  visibleColumns: PropTypes.array.isRequired,
  serverSideSearch: PropTypes.bool,
  onPageChange: PropTypes.func,
  onFilterChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  totalRows: PropTypes.number,
  initialPage: PropTypes.number,
};

function MobileRow({
  row,
  columns,
  index,
  isItemSelected,
  labelId,
  onClick,
  bodyCellStyle,
  bodyRowStyle,
  getRowStyle,
  checkBoxSelected,
  expandRow,
  expandComponent,
  page,
  rowsPerPage,
}) {
  const [open, setOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const globalIndex = page * rowsPerPage + index;
  const renderCellContent = (row, column, index) => {
    if (column.cell) return column.cell(row, index);
    if (column.selector) return column.selector(row, index);
    return row[column.id];
  };
  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        key={row.id}
        selected={isItemSelected}
        sx={{
          cursor: "pointer",
          ...bodyRowStyle,
          ...(getRowStyle && getRowStyle(row)),
        }}
      >
        {checkBoxSelected && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              checked={isItemSelected}
              onClick={(e) => {
                e.stopPropagation();
                onClick(e, row);
              }}
              inputProps={{
                "aria-labelledby": labelId,
              }}
            />
          </TableCell>
        )}
        <TableCell
          component="th"
          id={labelId}
          scope="row"
          sx={{ width: "50px", fontWeight: "bold" }}
        >
          {globalIndex + 1}
        </TableCell>
        <TableCell
          component="th"
          id={labelId}
          scope="row"
          sx={{ p: 1, width: "100%" }}
        >
          {renderCellContent(row, columns[1], index)}
        </TableCell>
        {expandRow && (
          <TableCell
            component="th"
            id={labelId}
            scope="row"
            sx={{ p: 1, width: "50px", ...bodyCellStyle }}
          >
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              <KeyboardArrowUp
                sx={(theme) => ({
                  rotate: !expanded ? "180deg" : "0deg",
                  transition: theme.transitions.create(["rotate"], {
                    easing: theme.transitions.easing.easeInOut,
                    duration: theme.transitions.duration.standard,
                  }),
                })}
              />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="details">
                <TableBody>
                  {columns.slice(1).map((column) => (
                    <TableRow key={column.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          ...bodyCellStyle,
                          color: "black",
                          fontWeight: "bold",
                        }}
                      >
                        {column.name || column.label}
                      </TableCell>
                      <TableCell align="right" sx={bodyCellStyle}>
                        {renderCellContent(row, column)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

MobileRow.propTypes = {
  row: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  isItemSelected: PropTypes.bool.isRequired,
  labelId: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  bodyCellStyle: PropTypes.object,
  bodyRowStyle: PropTypes.object,
  getRowStyle: PropTypes.func,
  checkBoxSelected: PropTypes.bool,
  expandRow: PropTypes.bool,
  expandComponent: PropTypes.func,
};

function CustomPaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const isFirstPage = page === 0;
  const isLastPage = page >= lastPage;

  const handlePageChange = (newPage) => (event) => {
    onPageChange(event, newPage);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
      <IconButton
        onClick={handlePageChange(0)}
        disabled={isFirstPage}
        aria-label="first page"
      >
        <KeyboardDoubleArrowLeftIcon />
      </IconButton>
      <IconButton
        onClick={handlePageChange(page - 1)}
        disabled={isFirstPage}
        aria-label="previous page"
      >
        <KeyboardArrowUp style={{ transform: "rotate(-90deg)" }} />
      </IconButton>
      <Typography variant="body2" sx={{ mx: 1 }}>
        {page + 1}
      </Typography>
      <IconButton
        onClick={handlePageChange(page + 1)}
        disabled={isLastPage}
        aria-label="next page"
      >
        <KeyboardArrowUp style={{ transform: "rotate(90deg)" }} />
      </IconButton>
      <IconButton
        onClick={handlePageChange(lastPage)}
        disabled={isLastPage}
        aria-label="last page"
      >
        <KeyboardDoubleArrowLeftIcon style={{ transform: "rotate(-180deg)" }} />
      </IconButton>
    </Box>
  );
}
export default function EnhancedTable({
  rows = [],
  columns = null,
  expandRow = false,
  expandComponent,
  tableHeader = true,
  title = "Table",
  defaultSort = "calories",
  defaultOrder = "asc",
  pagination = true,
  rowsPerPageOptions = [50, 100, 500],
  defaultRowsPerPage = 50,
  onDeleteSelected,
  compactStyles = {
    headerCellStyle: {
      padding: "4px 5px",
      fontSize: "12px",
    },
    bodyCellStyle: {
      padding: "3px 5px",
      fontSize: "12px",
    },
    headerRowStyle: {
      height: "20px",
    },
    bodyRowStyle: {
      height: "20px",
    },
  },
  checkBoxSelected = false,
  selectedConfiguration = {
    bgColor: null,
    button: null,
  },
  getRowStyle,
  globalSearch = true,
  loading = false,
  loadingComponent = null,
  serverSidePagination = false,
  serverSideSearch = false,
  onPageChange: onServerPageChange,
  onSearchChange,
  totalRows = 0,
  initialPage = 1,
  fetchData = null,
  showColumn = true,
  sx,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [order, setOrder] = React.useState(defaultOrder);
  const [orderBy, setOrderBy] = React.useState(defaultSort);
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [expandedRows, setExpandedRows] = React.useState({});
  const [visibleColumns, setVisibleColumns] = React.useState(
    columns?.map((col) => col.id)
  );
  const [searchText, setSearchText] = React.useState("");
  const toggleColumn = (columnId) => {
    setVisibleColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  };
  const filteredColumns = columns?.filter((col) =>
    visibleColumns?.includes(col.id)
  );

  const filteredRows = React.useMemo(() => {
    if (serverSideSearch) {
      return rows;
    }
    const lowercasedSearch = searchText?.toLowerCase()?.trim();
    return rows?.filter((row) => {
      return columns?.some((column) => {
        let cellValue;
        if (column?.value) {
          cellValue = column?.value(row);
        } else if (column.selector) {
          cellValue = column?.selector(row);
        } else {
          cellValue = row[column.id];
        }
        return String(cellValue).toLowerCase().includes(lowercasedSearch);
      });
    });
  }, [rows, searchText, columns, serverSideSearch]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const getExportData = async (fetchData) => {
    if (fetchData) {
      const response = await fetchData?.fnc(fetchData?.params);
      return response?.data || [];
    } else {
      return filteredRows;
    }
  };
  const exportToPDF = async () => {
    applyPlugin(jsPDF);
    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const tableTitle = title || "Table";
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 14;
      const availableWidth = pageWidth - margin * 2;

      const exportColumns = columns.filter(
        (column) => column.id !== "id" && column.id !== "actions"
      );
      const exportDataRaw = await getExportData(fetchData);
      const exportData = exportDataRaw.map((row) => {
        return exportColumns.map((column) => {
          let computedValue;

          if (column?.value) {
            computedValue = column?.value(row);
          } else if (column.selector) {
            computedValue = column?.selector(row);
          } else if (column.cell) {
            computedValue = column?.cell(row);
          } else {
            computedValue = row[column.id];
          }

          return computedValue != null ? String(computedValue) : "";
        });
      });

      const headers = exportColumns.map(
        (column) => column.name || column.label || column.id
      );

      // Calculate column widths
      const columnCount = headers.length;
      let columnWidths = [];

      if (columnCount > 0) {
        // Content-based width calculation
        columnWidths = headers.map((header, colIndex) => {
          const columnValues = exportData.map((row) => row[colIndex]);
          const allValues = [header, ...columnValues];
          const longestText = allValues.reduce((a, b) =>
            a.length > b.length ? a : b
          );
          const baseWidth = longestText.length * 1.5;
          return Math.min(Math.max(baseWidth, 20), availableWidth / 2);
        });

        // Normalize widths to fit available space
        const totalCalculatedWidth = columnWidths.reduce((a, b) => a + b, 0);
        const scaleFactor = availableWidth / totalCalculatedWidth;
        columnWidths = columnWidths.map((width) => width * scaleFactor);
      }

      // Add title
      doc.text(tableTitle, margin, 15);

      // Generate table with dynamic column widths
      doc.autoTable({
        head: [headers],
        body: exportData,
        startY: 20,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 8,
          overflow: "linebreak",
          valign: "middle",
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        columnStyles: columnWidths.reduce((styles, width, index) => {
          styles[index] = { cellWidth: width };
          return styles;
        }, {}),
        tableWidth: "auto",
        pageBreak: "auto",
      });

      // Save the PDF
      doc.save(
        `${tableTitle.replace(/\s+/g, "_")}_${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`
      );
    } catch (error) {
      alert(`Error generating PDF. Please try again.${error}`);
    }
  };
  const isDarkMode = false;

  const exportToExcel = async () => {
    const tableTitle = title || "Table";

    // Filter out columns with id 'id' or 'actions'
    const exportColumns = filteredColumns.filter(
      (column) => column.id !== "id" && column.id !== "actions"
    );

    // Prepare headers from the filtered columns
    const headers = exportColumns.map((column) => ({
      header: column.name || column.label || column.id,
      key: column.id,
    }));

    // Create export data using only the filtered columns
    const exportDataRaw = await getExportData(fetchData);

    // Initialize totals object for number columns
    const columnTotals = {};
    exportColumns.forEach((column) => {
      const key = column.id;
      if (column.type === "number" || column.type === "Number") {
        columnTotals[key] = 0;
      }
    });

    const exportData = exportDataRaw.map((row) => {
      const rowData = {};
      exportColumns.forEach((column) => {
        const key = column.id;
        let value;

        // Priority 1: column.value (if it's a function)
        if (column?.value) {
          value = column.value(row);
        }
        // Priority 2: column.selector
        else if (column.selector) {
          value = column.selector(row);
        }
        // Priority 3: column.cell
        else if (column.cell) {
          value = column.cell(row);
        }
        // Priority 4: row[column.id] (fallback)
        else {
          value = row[column.id];
        }

        // Ensure null/undefined becomes an empty string
        if (value == null) {
          value = "";
        }

        // Add to total if it's a number column and has a valid number
        if (
          (column.type === "number" || column.type === "Number") &&
          value !== "" &&
          !isNaN(Number(value))
        ) {
          columnTotals[key] += Number(value);
        }

        rowData[key] = value;
      });
      return rowData;
    });

    // Create total row only if there are number columns with totals
    const hasNumberColumns = Object.keys(columnTotals).length > 0;

    if (hasNumberColumns) {
      const totalRow = {};
      exportColumns.forEach((column) => {
        const key = column.id;

        if (column.type === "number" || column.type === "Number") {
          // Format the total (remove trailing .00 if it's a whole number)
          const total = columnTotals[key] || 0;
          totalRow[key] = Number.isInteger(total)
            ? total
            : Number(total.toFixed(2));
        } else if (column.id === exportColumns[0]?.id) {
          // Add "Total" label to first column
          totalRow[key] = "Total";
        } else {
          totalRow[key] = "";
        }
      });

      // Add total row to exportData
      exportData.push(totalRow);
    }

    // Create worksheet with headers
    const worksheet = XLSX.utils.json_to_sheet(exportData, {
      header: headers.map((h) => h.key),
    });

    // Customize header names in Excel
    XLSX.utils.sheet_add_aoa(worksheet, [headers.map((h) => h.header)], {
      origin: "A1",
    });

    // Style the total row if exists
    if (hasNumberColumns) {
      const totalRowIndex = exportData.length; // Total row is the last row (1-indexed for Excel)
      const range = XLSX.utils.decode_range(worksheet["!ref"]);

      // Style total row - make it bold
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({
          r: totalRowIndex,
          c: col,
        });
        if (worksheet[cellAddress]) {
          // Create a bold font style
          worksheet[cellAddress].s = {
            font: {
              bold: true,
            },
            // Optional: add background color
            // fill: {
            //   fgColor: { rgb: "F2F2F2" }
            // }
          };
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(
      workbook,
      `${tableTitle.replace(/\s+/g, "_")}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };
  // const exportToExcel = async () => {
  //   const tableTitle = title || "Table";

  //   // Filter out columns with id 'id' or 'actions'
  //   const exportColumns = filteredColumns.filter(
  //     (column) => column.id !== "id" && column.id !== "actions"
  //   );

  //   // Prepare headers from the filtered columns
  //   const headers = exportColumns.map((column) => ({
  //     header: column.name || column.label || column.id,
  //     key: column.id,
  //   }));

  //   // Create export data using only the filtered columns
  //   const exportDataRaw = await getExportData(fetchData);
  //   const exportData = exportDataRaw.map((row) => {
  //     const rowData = {};
  //     exportColumns.forEach((column) => {
  //       const key = column.id;

  //       // Priority 1: column.value (if it's a function)
  //       if (column?.value) {
  //         rowData[key] = column.value(row);
  //       }
  //       // Priority 2: column.selector
  //       else if (column.selector) {
  //         rowData[key] = column.selector(row);
  //       }
  //       // Priority 3: column.cell
  //       else if (column.cell) {
  //         rowData[key] = column.cell(row);
  //       }
  //       // Priority 4: row[column.id] (fallback)
  //       else {
  //         rowData[key] = row[column.id];
  //       }

  //       // Ensure null/undefined becomes an empty string
  //       if (rowData[key] == null) {
  //         rowData[key] = "";
  //       }
  //     });
  //     return rowData;
  //   });

  //   // Create worksheet with headers
  //   const worksheet = XLSX.utils.json_to_sheet(exportData, {
  //     header: headers.map((h) => h.key),
  //   });

  //   // Customize header names in Excel
  //   XLSX.utils.sheet_add_aoa(worksheet, [headers.map((h) => h.header)], {
  //     origin: "A1",
  //   });

  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  //   XLSX.writeFile(
  //     workbook,
  //     `${tableTitle.replace(/\s+/g, "_")}_${new Date()
  //       .toISOString()
  //       .slice(0, 10)}.xlsx`
  //   );
  // };
  const handleSelectAllClick = async (event) => {
    if (event.target.checked) {
      const exportDataRaw = await getExportData(fetchData);

      setSelected(exportDataRaw);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = fetchData
      ? selected?.findIndex(
          (item) => item[fetchData?.params.key] === id[fetchData?.params?.key]
        )
      : selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    if (serverSidePagination) {
      if (onServerPageChange) {
        onServerPageChange({
          page: newPage + 1,
          rowsPerPage,
          searchText,
        });
      }
    }

    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (serverSidePagination) {
      if (onServerPageChange) {
        onServerPageChange({
          page: 1, // Reset to first page when changing rows per page
          rowsPerPage: newRowsPerPage,
          searchText,
        });
      }
    }
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = React.useCallback(
    (text) => {
      if (serverSideSearch) {
        if (onSearchChange) {
          onSearchChange(text?.trim(), {
            page: 1,
            rowsPerPage,
          });
        }
      } else {
        setPage(0);
        setSearchText(text?.trim());
      }
    },
    [searchText]
  );

  const handleDeleteSelected = () => {
    if (onDeleteSelected) {
      onDeleteSelected(selected);
    }
    setSelected([]);
    return selected;
  };
  const handleExpandClick = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderCellContent = (row, column, index) => {
    const globalIndex = page * rowsPerPage + index;

    if (column.cell) return column.cell(row, globalIndex);
    if (column.selector) return column.selector(row, globalIndex);
    return row[column.id];
  };
  const visibleRows = React.useMemo(() => {
    if (serverSidePagination) {
      // Server is handling pagination, so we use all rows
      return [...rows].sort(getComparator(order, orderBy));
    }
    // Client-side pagination
    return [...(filteredRows || [])]
      .sort(getComparator(order, orderBy))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [
    order,
    orderBy,
    page,
    rowsPerPage,
    filteredRows,
    serverSidePagination,
    rows,
  ]);

  const totalRowCount = serverSidePagination ? totalRows : filteredRows?.length;
  useEffect(() => {
    setVisibleColumns(columns?.map((col) => col.id));
  }, [columns]);

  useEffect(() => {
    if (serverSidePagination) {
      setPage(initialPage > 0 ? initialPage - 1 : 0);
    }
  }, [initialPage, serverSidePagination]);

  return (
    <Box sx={sx}>
      <Paper
        sx={{
          width: "100%",
          mb: 2,
          bgcolor: isDarkMode ? "background.default" : "background.paper",
          color: isDarkMode ? "text.primary" : "text.secondary",
        }}
      >
        {tableHeader && (
          <EnhancedTableToolbar
            numSelected={selected.length}
            selectedConfiguration={selectedConfiguration}
            title={title}
            onDelete={handleDeleteSelected}
            onSearch={handleSearchChange}
            globalSearch={globalSearch}
            onExportPDF={exportToPDF}
            onExportExcel={exportToExcel}
            columns={columns}
            onToggleColumn={toggleColumn}
            visibleColumns={visibleColumns}
          />
        )}
        {columns?.length === 0 || !columns ? (
          <BTTypography variant="body2" sx={{ textAlign: "center", p: 3 }}>
            The column definitions are missing or not properly initialized 😓.
          </BTTypography>
        ) : (
          <TableContainer sx={{ maxHeight: 650, pl: 2, pr: 2 }}>
            <Table
              aria-labelledby="tableTitle"
              size="small"
              aria-label="a dense table"
              stickyHeader
            >
              {!isMobile && showColumn && (
                <EnhancedTableHead
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={fetchData ? totalRows : filteredRows?.length}
                  columns={filteredColumns}
                  headerCellStyle={compactStyles?.headerCellStyle}
                  headerRowStyle={compactStyles?.headerRowStyle}
                  checkBoxSelected={checkBoxSelected}
                  expandRow={expandRow}
                />
              )}
              {loading ? (
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={
                        filteredColumns.length +
                        (checkBoxSelected ? 1 : 0) +
                        (expandRow ? 1 : 0)
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100px", // Adjust height as needed
                          width: "100%",
                        }}
                      >
                        {loadingComponent || (
                          <CircularProgress
                            sx={(theme) => ({
                              color: "#1a90ff",
                              animationDuration: "1000ms",
                              [`& .${circularProgressClasses.circle}`]: {
                                strokeLinecap: "round",
                              },
                              ...theme.applyStyles("dark", {
                                color: "#308fe8",
                              }),
                            })}
                            size={40}
                            thickness={4}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : rows?.length === 0 || !rows || filteredRows?.length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={
                        filteredColumns.length +
                        (checkBoxSelected ? 1 : 0) +
                        (expandRow ? 1 : 0)
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100px", // Adjust height as needed
                          width: "100%",
                        }}
                      >
                        <BTTypography variant="body2">
                          📭 Oops! No data found.
                        </BTTypography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {visibleRows.map((row, index) => {
                    const isItemSelected = fetchData
                      ? selected.some(
                          (item) =>
                            item[fetchData?.params?.key] ===
                            row[fetchData?.params.key]
                        )
                      : selected.includes(row);

                    const labelId = `enhanced-table-checkbox-${index}`;
                    const isExpanded = expandedRows[index] || false;
                    return isMobile ? (
                      <MobileRow
                        key={index}
                        index={index}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        row={row}
                        columns={columns}
                        isItemSelected={isItemSelected}
                        labelId={labelId}
                        onClick={handleClick}
                        bodyCellStyle={compactStyles?.bodyCellStyle}
                        bodyRowStyle={compactStyles?.bodyRowStyle}
                        getRowStyle={getRowStyle}
                        checkBoxSelected={checkBoxSelected}
                        expandRow={expandRow}
                        expandComponent={expandComponent}
                      />
                    ) : (
                      <React.Fragment key={index}>
                        <TableRow
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          selected={isItemSelected}
                          sx={{
                            ...compactStyles?.bodyRowStyle,
                            ...(getRowStyle && getRowStyle(row)),
                            height: "20px",
                          }}
                        >
                          {checkBoxSelected && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                disabled={
                                  row?.ret_bill_status
                                    ? row?.ret_bill_status === 2
                                      ? true
                                      : false
                                    : false
                                }
                                onClick={(e) => handleClick(e, row)}
                                // inputProps={{
                                //   "aria-labelledby": labelId,
                                // }}
                              />
                            </TableCell>
                          )}
                          {expandRow && (
                            <TableCell
                              sx={{ width: "10px" }}
                              padding="checkbox"
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleExpandClick(index)}
                              >
                                <KeyboardArrowUp
                                  sx={(theme) => ({
                                    rotate: !isExpanded ? "180deg" : "0deg",
                                    transition: theme.transitions.create(
                                      ["rotate"],
                                      {
                                        easing:
                                          theme.transitions.easing.easeInOut,
                                        duration:
                                          theme.transitions.duration.shorter,
                                      }
                                    ),
                                  })}
                                />
                              </IconButton>
                            </TableCell>
                          )}
                          {filteredColumns.map((column) => (
                            <TableCell
                              key={column.id}
                              component="th"
                              scope="row"
                              align={
                                column?.center
                                  ? "center"
                                  : column.numeric
                                  ? "right"
                                  : "left"
                              }
                              padding={
                                column.disablePadding ? "none" : "normal"
                              }
                              sx={{
                                ...compactStyles?.bodyCellStyle,
                                ...column.bodyCellStyle,
                                height: "20px",
                                width: column.width,
                                // overflow: "hidden",
                              }}
                            >
                              {renderCellContent(row, column, index)}
                            </TableCell>
                          ))}
                        </TableRow>
                        {expandRow && isExpanded && (
                          <TableRow>
                            <TableCell
                              sx={{ p: 0 }}
                              colSpan={
                                columns.length +
                                (checkBoxSelected ? 1 : 0) +
                                (expandRow ? 1 : 0)
                              }
                            >
                              {expandComponent &&
                                React.cloneElement(expandComponent, {
                                  row,
                                })}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        )}
        {pagination && Array.isArray(rows) && rows.length > 0 && (
          <TablePagination
            component="div"
            rowsPerPageOptions={rowsPerPageOptions}
            count={totalRowCount || 0}
            rowsPerPage={rowsPerPage}
            page={
              Math.min(
                page,
                Math.max(0, Math.ceil(totalRowCount / rowsPerPage) - 1)
              ) || 0
            }
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={CustomPaginationActions} // Use custom actions
            sx={{
              "& .MuiTablePagination-toolbar": {
                minHeight: "30px",
                padding: "0 8px",
              },
              "& .MuiTablePagination-selectLabel": { fontSize: "0.90rem" },
              "& .MuiTablePagination-displayedRows": { fontSize: "0.90rem" },
              "& .MuiInputBase-root": { fontSize: "0.90rem" },
              "& .MuiMenuItem-root": { fontSize: "0.90rem", minHeight: "32px" },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: { "& .MuiMenuItem-root": { fontSize: "0.90rem" } },
                },
              },
            }}
          />
        )}
      </Paper>
    </Box>
  );
}

EnhancedTable.propTypes = {
  rows: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      label: PropTypes.string,
      selector: PropTypes.func,
      cell: PropTypes.func,
      numeric: PropTypes.bool,
      sortable: PropTypes.bool,
      disablePadding: PropTypes.bool,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      headerCellStyle: PropTypes.object,
      bodyCellStyle: PropTypes.object,
    })
  ),
  title: PropTypes.string,
  defaultSort: PropTypes.string,
  defaultOrder: PropTypes.oneOf(["asc", "desc"]),
  pagination: PropTypes.bool,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  defaultRowsPerPage: PropTypes.number,
  onDeleteSelected: PropTypes.func,
  getRowStyle: PropTypes.func,
  checkBoxSelected: PropTypes.bool,
  expandComponent: PropTypes.object,
  expandRow: PropTypes.bool,
  globalSearch: PropTypes.bool,
  compactStyles: PropTypes.object,
  tableHeader: PropTypes.bool,
  loading: PropTypes.bool,
  loadingComponent: PropTypes.node,
};
